// Central HTTP client for the RIMON backend (NestJS, ../backend).
// Contract: docs/API-CONTRACT.md + ../backend/docs/FRONTEND_INTEGRATION.md.
//
// - Base URL from VITE_API_URL (default http://localhost:3200/api/v1); a path (`/api/v1`) = same origin as the page,
//   used by the build the API serves itself (backend/apps/api/src/web.ts)
// - `X-Client: web|desktop` on every request
// - Access token in memory only, sent as Bearer
// - Sign-in through RimoID. Refresh: web = httpOnly cookie `rimon_refresh` (credentials: 'include', the API is the
//   backend-for-frontend); desktop = refresh token in the OS keychain, renewed by Rust (desktop/rimoid.ts)
// - On 401: one refresh (parallel refreshes coalesced), then one retry
// - Responses are unwrapped; only paginated lists are { data, meta }
// - Errors: { statusCode, code, message, details?, requestId? }
import type { ApiErrorBody, Page } from '@rimon/contracts';
import { isDesktop } from '../platform';
import { desktopRefresh } from '../desktop/rimoid';

/** The API's base: the desktop app's server address (settings.rs sets `window.__RIMO_SERVER__` before the page runs, release builds only), else the build's. */
export const apiBase = (server: string | undefined, built: string | undefined) => (server ? `${server}/api/v1` : built || 'http://localhost:3200/api/v1').replace(/\/$/, '');
const RAW_API = apiBase(typeof window !== 'undefined' ? (window as { __RIMO_SERVER__?: string }).__RIMO_SERVER__ : undefined, import.meta.env.VITE_API_URL as string | undefined);
const CONFIGURED_API = RAW_API.startsWith('/') && typeof location !== 'undefined' && /^https?:$/.test(location.protocol) ? location.origin + RAW_API : RAW_API;
const isLocalHost = (h: string) => h === 'localhost' || h === '127.0.0.1' || /^(10|192\.168|172\.(1[6-9]|2\d|3[01]))\./.test(h);
/**
 * The API on the same host name as the page when both are local: `./rimo.sh start` points VITE_API_URL at the LAN
 * address (for the iPad), but a page opened as http://localhost:3201 would then call another site and the browser
 * drops the session cookie (SameSite) — every reload signed you out. The API listens on both names.
 */
export function resolveApiUrl(configured: string, page: { protocol: string; hostname: string } | null): string {
  try {
    const u = new URL(configured);
    if (!page || !/^https?:$/.test(page.protocol) || !isLocalHost(u.hostname) || !isLocalHost(page.hostname) || u.hostname === page.hostname) return configured;
    u.hostname = page.hostname;
    return u.toString().replace(/\/$/, '');
  } catch { return configured; }
}
export const API_URL = resolveApiUrl(CONFIGURED_API, typeof location !== 'undefined' ? location : null);
export const MOCK = import.meta.env.VITE_DATA_MODE === 'mock';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(message);
  }
}

/** Before RimoID the desktop app kept its refresh token here; it is removed (the keychain holds RimoID's now). */
const LEGACY_REFRESH_KEY = 'rimon.refresh';
try { if (isDesktop()) localStorage.removeItem(LEGACY_REFRESH_KEY); } catch { /* storage unavailable */ }
let accessToken: string | null = null;
type TokenListener = (token: string | null) => void;
const tokenListeners = new Set<TokenListener>();

export const tokens = {
  get access() {
    return accessToken;
  },
  /** Only the access token lives in JavaScript; refresh tokens are in the cookie (web) or the keychain (desktop). */
  set(access: string | null, _refresh?: string | null) {
    accessToken = access;
    tokenListeners.forEach((l) => l(access));
  },
  get refresh(): string | null {
    return null;
  },
  /** Socket.IO reconnects need the freshest token. */
  onChange(l: TokenListener): () => void {
    tokenListeners.add(l);
    return () => {
      tokenListeners.delete(l);
    };
  },
};

export const clientHeaders = (): Record<string, string> => ({ 'X-Client': isDesktop() ? 'desktop' : 'web' });

async function parse(res: Response): Promise<any> {
  if (res.status === 204) return undefined;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json().catch(() => null) : res.text();
}

function toError(status: number, body: any, fallback: string): ApiError {
  const e = body && typeof body === 'object' && 'code' in body ? (body as ApiErrorBody) : null;
  return e ? new ApiError(status, e.code, e.message, e.details, e.requestId) : new ApiError(status, 'http_' + status, fallback);
}

let refreshing: Promise<boolean> | null = null;
let onRefreshed: ((user: any) => void) | null = null;
let onLost: (() => void) | null = null;
let onPasswordRequired: (() => void) | null = null;
/** auth.ts keeps the session user in sync with refreshes, and drops it when the server ends the session. */
export const setSessionHooks = (h: { refreshed: (user: any) => void; lost: () => void; passwordRequired: () => void }) => {
  onRefreshed = h.refreshed;
  onLost = h.lost;
  onPasswordRequired = h.passwordRequired;
};

/**
 * Secondary desktop windows (Pay-Assistant) must not rotate the refresh token
 * themselves — they ask the main window for a fresh access token instead.
 */
let refresher: (() => Promise<boolean>) | null = null;
export const setRefresher = (fn: () => Promise<boolean>) => {
  refresher = fn;
};

/** Exchanges the refresh token for a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<boolean> {
  if (refresher) return refresher();
  refreshing ??= (async () => {
    try {
      if (isDesktop()) {
        // Rust renews with RimoID (refresh token from the keychain); the session user comes from the API.
        const hadSession = !!accessToken;
        const fresh = await desktopRefresh().catch(() => null);
        const me = fresh ? await fetch(API_URL + '/auth/me', { headers: { ...clientHeaders(), Authorization: 'Bearer ' + fresh.accessToken } }).catch(() => null) : null;
        if (!fresh || !me?.ok) {
          tokens.set(null, null);
          if (hadSession) onLost?.();
          return false;
        }
        tokens.set(fresh.accessToken);
        onRefreshed?.(await parse(me));
        return true;
      }
      const refresh = tokens.refresh;
      const call = () => fetch(API_URL + '/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { ...clientHeaders(), ...(refresh ? { 'Content-Type': 'application/json' } : {}) },
        body: refresh ? JSON.stringify({ refreshToken: refresh }) : undefined,
      });
      let res = await call();
      // Web: another tab may have rotated the cookie a moment ago; the retry sends the new one.
      if (res.status === 401 && !refresh) { await new Promise((r) => setTimeout(r, 800)); res = await call(); }
      if (!res.ok) {
        // Role change / deactivation / expiry: the server revoked the session.
        const hadSession = !!accessToken;
        tokens.set(null, null);
        if (hadSession) onLost?.();
        return false;
      }
      const body = await parse(res);
      tokens.set(body.accessToken, body.refreshToken ?? undefined);
      onRefreshed?.(body.user);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Set by BankAccountDialog: asks for the missing account; true = created, the call is repeated once. */
let missingAccount: ((bank: string) => Promise<boolean>) | null = null, accountAsked = false;
export const onMissingAccount = (f: typeof missingAccount) => { missingAccount = f; };

export interface RequestOpts {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  auth?: boolean;
  signal?: AbortSignal;
}

async function send(path: string, opts: RequestOpts, retried: boolean): Promise<any> {
  const { method = 'GET', body, query, auth = true, signal } = opts;
  const qs = Object.entries(query || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(v)))
    .join('&');
  const headers: Record<string, string> = { Accept: 'application/json', ...clientHeaders() };
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (auth && accessToken) headers.Authorization = 'Bearer ' + accessToken;

  let res: Response;
  try {
    res = await fetch(API_URL + path + (qs ? '?' + qs : ''), {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiError(0, 'network', 'Server nicht erreichbar.');
  }

  if (res.status === 401 && auth && !retried && (await refreshSession())) return send(path, opts, true);
  const parsed = await parse(res);
  if (!res.ok) {
    const err = toError(res.status, parsed, res.statusText);
    if (err.code === 'password_change_required') onPasswordRequired?.();
    // A payment needs the bank's account: offer to create it, then the same call goes on (components/bank/BankAccountDialog).
    const bank = (err.details as { bank?: string } | undefined)?.bank;
    if (err.code === 'bank_account_not_found' && bank && missingAccount && !accountAsked) {
      accountAsked = true;
      try { if (await missingAccount(bank)) return await send(path, opts, retried); } finally { accountAsked = false; }
    }
    throw err;
  }
  return parsed;
}

/** POST returning a file (PDF/ZIP) with the same auth, refresh and error handling as `api`; the server names the file. */
export async function apiBytes(path: string, body: unknown): Promise<{ bytes: Uint8Array; filename: string; type: string }> {
  const call = () => fetch(API_URL + path, { method: 'POST', credentials: 'include', body: JSON.stringify(body),
    headers: { ...clientHeaders(), 'Content-Type': 'application/json', ...(accessToken ? { Authorization: 'Bearer ' + accessToken } : {}) } });
  let res: Response;
  try { res = await call(); if (res.status === 401 && (await refreshSession())) res = await call(); }
  catch { throw new ApiError(0, 'network', 'Server nicht erreichbar.'); }
  if (!res.ok) throw toError(res.status, await parse(res), res.statusText);
  const cd = res.headers.get('content-disposition') || '', m = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  return { bytes: new Uint8Array(await res.arrayBuffer()), filename: m ? decodeURIComponent(m[1]!) : 'download', type: res.headers.get('content-type') || 'application/octet-stream' };
}

export function api<T = any>(path: string, opts: RequestOpts = {}): Promise<T> {
  return send(path, opts, false);
}

/** Walks every page of a paginated list endpoint (pageSize ≤ 100). */
export async function apiAll<T = any>(path: string, query: RequestOpts['query'] = {}): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; ; page++) {
    const r = await api<Page<T>>(path, { query: { ...query, page, pageSize: 100 } });
    out.push(...r.data);
    if (!r.meta || page >= r.meta.pageCount) return out;
  }
}

/** German message for a failed call (toasts). */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const byCode: Record<string, string> = {
      network: 'Server nicht erreichbar.',
      forbidden: 'Für deine Rolle gesperrt.',
      not_found: 'Nicht gefunden.',
      no_file: 'Zu diesem Dokument gibt es keine Datei.',
      pdf_unreadable: 'Die PDF dieser Rechnung lässt sich für die Vermerke nicht lesen.',
      version_conflict: 'Wurde inzwischen geändert · Daten neu geladen.',
      already_paid: 'Ist bereits bezahlt · Daten neu geladen.',
      pdf_password_protected: 'Das PDF ist mit einem Passwort geschützt · bitte ohne Passwort speichern.',
      statement_future_month: 'Der Auszug ist für einen Monat, der noch nicht begonnen hat · falsche Datei?',
      payment_reconciled: 'Die Zahlung ist schon einer Buchung im Extrait zugeordnet · dort zuerst lösen.',
      not_printed: 'Muss zuerst gedruckt sein (Imprimé).',
      not_payable_status: 'In diesem Status kann das Dokument nicht bezahlt werden.',
      not_paid: 'Nur bezahlte Rechnungen werden klassiert.',
      nothing_to_print: 'In diesem Monat gibt es nichts mehr zu drucken.',
      chantiers_unreachable: 'RimoChantiers ist gerade nicht erreichbar.',
      no_net: 'Die Rechnung hat noch keinen Nettobetrag – zuerst im Dokument ergänzen.',
      not_sent: 'Diese Bestellung ist in RimoChantiers noch nicht gesendet.',
      order_not_found: 'Diese Bestellung gibt es in RimoChantiers nicht (mehr).',
      not_payable: 'Dieses Dokument wird nicht über Bezahlung bezahlt (z. B. Ausgangsrechnung).',
      invalid_status_transition: 'Dieser Status passt nicht zu diesem Dokumenttyp.',
      invalid_amount: 'Montant payée ist keine gültige Zahl.',
      bank_account_not_found: ({
        BIL: 'Eine Zahlung wird auf ein Bankkonto gebucht – lege das Konto von BIL einmal an (Firma → Bankkonten).',
        BGL: 'Eine Zahlung wird auf ein Bankkonto gebucht – lege das Konto von BGL einmal an (Firma → Bankkonten).',
      } as Record<string, string>)[(e.details as { bank?: string } | undefined)?.bank ?? ''] ?? 'Eine Zahlung wird auf ein Bankkonto gebucht – lege das Konto dieser Bank einmal an (Firma → Bankkonten).',
      bank_locked: 'Dieses Konto hat Kontoauszüge · seine Bank lässt sich nicht mehr ändern.',
      use_known_bank: 'BIL und BGL werden als Bank gewählt, nicht unter „Andere“; VISA ist eine Karte.',
      iban_missing: 'Ohne IBAN steht ein Konto nicht auf Dokumenten · zuerst die IBAN eintragen.',
      rate_limited: 'Zu viele Anfragen · bitte kurz warten.',
      validation_failed: 'Eingabe ungültig · bitte prüfen.',
      password_change_required: 'Bitte zuerst das Passwort ändern.',
      period_closed: 'Der Monat ist abgeschlossen · zuerst wieder öffnen.',
      closing_incomplete: 'Abschluss noch unvollständig · offene Punkte prüfen.',
      matricule_taken: 'Diese Matricule hat schon ein anderer Mitarbeiter.',
      email_in_use: 'Diese E-Mail hat schon einen Zugang.',
      app_access_exists: 'Dieser Mitarbeiter hat schon einen App-Zugang.',
      unique_violation: 'Dieser Wert ist schon vergeben.',
      invalid_iban: 'Die IBAN ist ungültig (Prüfziffer stimmt nicht). Bitte Tippfehler prüfen.',
      too_many_on_documents: 'Höchstens 3 Konten auf Dokumenten.', bank_name_required: 'Bitte den Namen der Bank eintragen.',
      invalid_bic: 'Der BIC hat 8 oder 11 Zeichen.', account_archived: 'Dieses Konto ist archiviert.',
      card_exists: 'Für diese Bank gibt es schon eine aktive Karte mit diesen Endziffern.',
      user_already_linked: 'Dieses Konto ist schon mit einem anderen Mitarbeiter verbunden.',
      moved_to_rimoid: 'Konten, Rollen und Rechte werden jetzt im RimoID-Panel verwaltet.',
      employee_in_use: 'Mitarbeiter hat Dokumente, Firmenkarten oder Apple-Pay-Geräte und kann nicht gelöscht werden · als ausgetreten markieren.',
      employee_self: 'Den eigenen Mitarbeiter-Eintrag kannst du nicht löschen.',
      card_dates: '„Gültig ab“ darf nicht nach dem Ablaufmonat der Karte liegen.',
      instrument_exists: 'Für diese Karte gibt es schon ein Apple-Pay-Gerät mit diesen Endziffern.',
      code_recently_sent: 'Ein Code wurde gerade verschickt. Bitte kurz warten.',
      totp_not_started: 'Bitte die Einrichtung neu starten.',
      passkey_unknown: 'Dieser Passkey ist für RimoCompta nicht registriert.',
      passkey_invalid: 'Der Passkey konnte nicht geprüft werden. Bitte erneut versuchen.',
      passkey_expired: 'Die Anfrage ist abgelaufen. Bitte erneut versuchen.',
      passkey_exists: 'Dieser Passkey ist bereits hinterlegt.',
      totp_already_enabled: 'Zwei-Faktor-Anmeldung ist schon eingeschaltet. Zum Wechseln erst ausschalten.',
      account_locked: 'Zu viele Fehlversuche für dieses Konto. Bitte in 15 Minuten erneut versuchen.',
      sum_mismatch: 'Die Summe der Dokumente entspricht nicht dem Betrag der Buchung.',
      document_linked: 'Das Dokument ist schon einer anderen Buchung zugeordnet.',
      document_already_matched: 'Das Dokument ist schon einer anderen Buchung zugeordnet.',
      no_suggestion: 'Für diese Buchung gibt es keinen offenen Vorschlag mehr.',
      period_open: 'Die Downloads stehen nach dem Abschließen des Monats bereit.',
      preparation_cancelled: 'Diese Vorbereitung wurde abgebrochen.',
      reference_in_use: 'Wird noch verwendet und kann nicht entfernt werden.',
      employee_has_cards: 'Dem Mitarbeiter sind noch Karten oder Apple-Pay-Geräte zugeordnet.',
      role_in_use: 'Die Rolle ist noch Benutzern zugewiesen.',
      role_name_taken: 'Eine Rolle mit diesem Namen gibt es schon.',
      system_role: 'Systemrollen können nicht geändert werden.',
      card_not_found: 'Keine Karte mit diesen letzten 4 Ziffern gefunden.',
      credit_note_not_found: 'Note de crédit nicht gefunden.',
      credit_note_exhausted: 'Die Note de crédit ist schon vollständig verrechnet.',
      job_finished: 'Der Vorgang ist schon abgeschlossen.',
      export_not_ready: 'Export läuft noch · bitte gleich erneut versuchen.',
      batch_completed: 'Export ist schon fertig · die Rechnungen stehen auf Imprimé.',
      unknown_iban: 'Die IBAN aus dem Auszug ist unter Firma → Bankkonten nicht angelegt.',
      invalid_camt053: 'Die Datei ist kein gültiger CAMT.053-Auszug.',
      unsupported_statement_format: 'Nur CAMT.053 (XML) wird unterstützt.',
      payload_too_large: 'Die Datei ist zu groß.',
      unsupported_media_type: 'Dieser Dateityp wird nicht unterstützt (PDF, JPG, PNG, HEIC, TIFF oder ZIP).',
      too_many_records: 'Zu viele Einträge auf einmal.',
      preferences_too_large: 'Zu viele Einstellungen zum Speichern.',
      internal_error: 'Serverfehler · bitte erneut versuchen.',
      ai_not_configured: 'RimoAI ist nicht eingeschaltet · „Selbst zuordnen“ nutzen.',
      ai_unavailable: 'RimoAI nicht erreichbar (Ollama) · „Selbst zuordnen“ nutzen.',
      session_locked: 'Wird gerade von jemand anderem bearbeitet.',
      nothing_to_import: 'Noch nichts bestätigt.',
      unsorted_files: 'Zuerst allen Dateien einen Typ geben.',
      missing_fields: 'Pflichtfelder fehlen.',
      duplicate_document: 'Mögliches Duplikat.',
      undo_not_possible: 'Rückgängig nicht mehr möglich · schon bezahlt, vorbereitet oder Monat abgeschlossen.',
      session_has_imports: 'Aus diesem Import wurden schon Dokumente angelegt · rückgängig machen oder behalten?',
      import_incomplete: 'Noch nicht alle Dokumente bestätigt · nur die bestätigten importieren?',
      session_closed: 'Dieser Import ist schon abgeschlossen oder verworfen.',
      preparation_completed: 'Export ist schon fertig · die Rechnungen stehen auf Imprimé.',
      preparation_documents_unavailable: 'Einige Rechnungen sind nicht mehr Reçu oder schon in einer anderen Vorbereitung · Daten neu laden.',
      invalid_zip: 'Die ZIP-Datei ist beschädigt.',
      confirmation_required: 'Bezahlt oder abgeglichen · Änderung bitte bestätigen.',
      amounts_inconsistent: 'Netto + TVA ergibt nicht den Gesamtbetrag.',
      field_not_applicable: 'Dieses Feld gibt es für diesen Dokumenttyp nicht.',
      employee_not_found: 'Mitarbeiter nicht gefunden.',
      file_not_found: 'Datei nicht gefunden · bitte erneut hochladen.',
      payment_not_found: 'Zahlung nicht gefunden · Daten neu geladen.',
      document_reconciled: 'Mit einer Kontobuchung abgeglichen · zuerst den Haken unter Abschluss entfernen.',
      document_in_preparation: 'In einer offenen Vorbereitung · zuerst dort herausnehmen.',
      delete_confirmation_required: 'Bezahlt · Löschen samt Zahlung bitte bestätigen.',
      not_deletable: 'Kontoauszüge werden unter Abschluss verwaltet.',
      external_document: 'Kommt aus RimoChantiers · Daten dort ändern, hier nur Status und Notiz.',
      busy: 'Gerade werden zu viele PDFs erstellt · bitte gleich noch einmal.',
      too_many_tickets: 'Zu viele EscCalc-Tickets für ein PDF (höchstens 120) · weniger Rechnungen auswählen.',
      render_timeout: 'Das PDF hat zu lange gedauert · bitte noch einmal versuchen.',
      no_escompte: 'Keine der Rechnungen hat ein Escompte.',
      employee_name_required: 'Der Mitarbeiter braucht einen Namen.',
      file_already_imported: 'Aus dieser Datei wurden schon Dokumente importiert · zuerst rückgängig machen.',
      no_files: 'Der Import enthält noch keine Dateien.',
      not_editable: 'Kontoauszüge werden unter Abschluss bearbeitet.',
      not_marked_paid: 'Offene Dokumente werden unter Bezahlung bezahlt.',
      payment_exists: 'Das Dokument hat schon eine Zahlung · diese korrigieren.',
      payment_incomplete: 'Betrag, Datum und Konto der Zahlung fehlen.',
      rimoid_unavailable: 'RimoID ist gerade nicht erreichbar · bitte kurz warten.',
      duplicate_warning: 'Mögliches Duplikat.',
    };
    return byCode[e.code] || e.message;
  }
  return 'Unerwarteter Fehler.';
}

// Development: this module holds the session (user, tokens, listeners). A hot update would leave the running screens
// on the old copy (stale user, „lost“ reaching nobody) – reload the page instead.
if (import.meta.hot) import.meta.hot.accept(() => location.reload());
